// Phase 16.3 — LiftedAtTheMoment Admin Product Editor.
//
// Master admin / admin can browse the locally-mirrored 123-product Shopify
// catalog and edit the fields the storefront uses for routing, badges,
// pricing, and section assignment. No traffic ever leaves the platform for
// checkout — this site IS the storefront.

import React, { useEffect, useMemo, useState } from "react";
import { Search, Save, Loader2, X, ImagePlus, Tag, Sparkles } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";
import MediaUploader from "@/components/lifted/MediaUploader";

const SECTIONS = [
  { value: "",             label: "All",          color: "bg-white/10 text-white"  },
  { value: "gear",         label: "Gear",         color: "bg-lifted-green/15 text-lifted-green border-lifted-green/40" },
  { value: "accessories",  label: "Accessories",  color: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
  { value: "partner",      label: "Partner",      color: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  { value: "needs_review", label: "Needs Review", color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
];

function money(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function AdminProductsEditor() {
  const [list, setList] = useState({ products: [], counts: {}, total: 0 });
  const [q, setQ] = useState("");
  const [section, setSection] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selectedHandle, setSelectedHandle] = useState(null);

  const load = useMemo(() => async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/products", {
        params: { q: q || undefined, section: section || undefined, limit: 100 },
      });
      setList(data);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [q, section]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const selected = list.products.find((p) => p.handle === selectedHandle) || null;

  return (
    <AdminFeatureShell
      breadcrumb="LiftedAtTheMoment / Shop"
      title="Product Editor"
      description="Edit titles, pricing, section assignment, badges and images for the live storefront. Changes save instantly to MongoDB and propagate to /shop on the next page load."
      status={list.total ? `${list.total} products` : null}
      testId="admin-products-editor"
    >
      {/* Section pills */}
      <div className="flex flex-wrap gap-2">
        {SECTIONS.map((s) => {
          const count = s.value === "" ? list.counts.all : list.counts[s.value];
          const isActive = section === s.value;
          return (
            <button
              key={s.value || "all"}
              onClick={() => setSection(s.value)}
              className={`px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] transition ${
                isActive ? `${s.color} border` : "border-white/10 text-lifted-dim hover:text-white"
              }`}
              data-testid={`admin-products-section-${s.value || "all"}`}
            >
              {s.label} {typeof count === "number" && <span className="ml-1 opacity-70">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="glass-panel p-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-lifted-dim" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title, handle, vendor, tag…"
          className="flex-1 bg-transparent text-sm focus:outline-none"
          data-testid="admin-products-search"
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-lifted-dim" />}
      </div>

      {err && (
        <div className="glass-panel border border-rose-500/30 p-3 text-sm text-rose-200">{err}</div>
      )}

      <div className="grid lg:grid-cols-[1fr_420px] gap-6 items-start">
        {/* List */}
        <div className="space-y-2" data-testid="admin-products-list">
          {list.products.length === 0 && !loading && (
            <div className="glass-panel p-6 text-sm text-lifted-dim text-center">
              No products match. Try clearing filters.
            </div>
          )}
          {list.products.map((p) => (
            <button
              key={p.handle}
              onClick={() => setSelectedHandle(p.handle)}
              className={`w-full glass-panel p-3 text-left flex items-center gap-3 transition ${
                selectedHandle === p.handle ? "border-lifted-green/40" : "hover:border-white/20"
              }`}
              data-testid={`admin-products-row-${p.handle}`}
            >
              <div className="h-14 w-14 rounded-md overflow-hidden bg-black/40 shrink-0 grid place-items-center">
                {p.images?.[0]?.src ? (
                  <img src={p.images[0].src} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImagePlus className="h-5 w-5 text-lifted-dim" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-lifted-text truncate">{p.title}</div>
                <div className="text-[0.65rem] text-lifted-dim flex flex-wrap items-center gap-2 mt-1">
                  <span className="font-mono">{p.handle}</span>
                  <span>·</span>
                  <span>{money(p.price_min_cents)}</span>
                  <span>·</span>
                  <SectionChip value={p.section} />
                  {p.featured && <Badge>FEATURED</Badge>}
                  {p.new_release && <Badge>NEW</Badge>}
                  {p.best_seller && <Badge>BEST</Badge>}
                  {p.hidden && <Badge tone="rose">HIDDEN</Badge>}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Editor pane */}
        <div className="lg:sticky lg:top-0">
          {selected ? (
            <EditorPanel
              product={selected}
              onClose={() => setSelectedHandle(null)}
              onSaved={(updated) => {
                setList((l) => ({
                  ...l,
                  products: l.products.map((p) => (p.handle === updated.handle ? updated : p)),
                }));
              }}
            />
          ) : (
            <div className="glass-panel p-6 text-sm text-lifted-dim text-center">
              <Sparkles className="h-5 w-5 text-lifted-green mx-auto mb-2" />
              Select a product to edit.
            </div>
          )}
        </div>
      </div>
    </AdminFeatureShell>
  );
}

function Badge({ children, tone = "green" }) {
  const cls = tone === "rose" ? "border-rose-400/50 text-rose-300" : "border-lifted-green/40 text-lifted-green";
  return (
    <span className={`text-[0.55rem] tracking-[0.18em] font-bebas uppercase border rounded-full px-1.5 py-0.5 ${cls}`}>
      {children}
    </span>
  );
}

function SectionChip({ value }) {
  const cfg = SECTIONS.find((s) => s.value === (value || "needs_review")) || SECTIONS[4];
  return (
    <span className={`text-[0.55rem] tracking-[0.18em] font-bebas uppercase border rounded-full px-1.5 py-0.5 ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function EditorPanel({ product, onSaved, onClose }) {
  const [draft, setDraft] = useState(() => normalize(product));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  useEffect(() => { setDraft(normalize(product)); setOk(false); setErr(""); }, [product]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setBool = (k) => setDraft((d) => ({ ...d, [k]: !d[k] }));

  const save = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      // Build images array — uploaded media first (primary first), then any URL extras
      const uploaded = (draft.media || []).slice().sort((a, b) =>
        (b.primary ? 1 : 0) - (a.primary ? 1 : 0)
      ).map((m) => ({ src: m.url, upload_id: m.id, primary: !!m.primary }));
      const extras = (draft.image_urls_extra || "")
        .split("\n").map((s) => s.trim()).filter(Boolean).map((src) => ({ src }));
      const images = [...uploaded, ...extras];
      const payload = {
        title: draft.title,
        description: draft.description,
        vendor: draft.vendor,
        product_type: draft.product_type,
        price_min_cents: numOrNull(draft.price_min_cents),
        compare_at_price_cents: numOrNull(draft.compare_at_price_cents),
        section: draft.section || null,
        tags: draft.tags.split(",").map((t) => t.trim()).filter(Boolean),
        images,
        featured: !!draft.featured,
        new_release: !!draft.new_release,
        best_seller: !!draft.best_seller,
        same_day_eligible: !!draft.same_day_eligible,
        hidden: !!draft.hidden,
      };
      const { data } = await api.patch(`/admin/products/${draft.handle}`, payload);
      setOk(true);
      onSaved?.(data.product);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-panel p-5 space-y-4" data-testid="admin-products-editor-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-bebas tracking-[0.22em] text-[0.65rem] text-lifted-green">EDIT PRODUCT</div>
          <div className="text-sm text-lifted-text truncate">{draft.title}</div>
          <div className="text-[0.65rem] text-lifted-dim font-mono">{draft.handle}</div>
        </div>
        <button onClick={onClose} className="text-lifted-dim hover:text-white" aria-label="Close editor">
          <X className="h-4 w-4" />
        </button>
      </div>

      <FieldText label="Title" value={draft.title} onChange={(v) => set("title", v)} />
      <FieldText label="Vendor" value={draft.vendor} onChange={(v) => set("vendor", v)} />
      <FieldText label="Product type" value={draft.product_type} onChange={(v) => set("product_type", v)} />

      <div className="grid grid-cols-2 gap-3">
        <FieldNumber label="Price (¢)" value={draft.price_min_cents} onChange={(v) => set("price_min_cents", v)} />
        <FieldNumber label="Compare-at (¢)" value={draft.compare_at_price_cents} onChange={(v) => set("compare_at_price_cents", v)} />
      </div>

      <div>
        <Label>Section</Label>
        <div className="flex flex-wrap gap-2">
          {SECTIONS.filter((s) => s.value).map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => set("section", s.value)}
              className={`px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] ${
                draft.section === s.value ? `${s.color} border` : "border-white/10 text-lifted-dim hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <FieldText label="Tags (comma-separated)" icon={Tag} value={draft.tags} onChange={(v) => set("tags", v)} />

      <div>
        <Label>Product media</Label>
        <MediaUploader
          value={draft.media}
          onChange={(v) => set("media", v)}
          purpose="product_image"
          maxFiles={12}
          allowVideo={true}
          primaryEnabled={true}
          testId="admin-product-media"
        />
        <details className="mt-2 text-[0.65rem] text-lifted-dim">
          <summary className="cursor-pointer hover:text-white">Advanced: add image by URL</summary>
          <textarea
            value={draft.image_urls_extra}
            onChange={(e) => set("image_urls_extra", e.target.value)}
            rows={2}
            placeholder="https://...png&#10;one URL per line"
            className="mt-2 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs font-mono focus:border-lifted-green/40 focus:outline-none"
          />
        </details>
      </div>

      <FieldText label="Description" value={draft.description} onChange={(v) => set("description", v)} multiline />

      <div>
        <Label>Badges</Label>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Toggle on={draft.featured}          onClick={() => setBool("featured")}          label="Featured" />
          <Toggle on={draft.new_release}       onClick={() => setBool("new_release")}       label="New Release" />
          <Toggle on={draft.best_seller}       onClick={() => setBool("best_seller")}       label="Best Seller" />
          <Toggle on={draft.same_day_eligible} onClick={() => setBool("same_day_eligible")} label="Same-Day Eligible" />
          <Toggle on={draft.hidden}            onClick={() => setBool("hidden")}            label="Hidden" tone="rose" />
        </div>
      </div>

      {err && <div className="text-xs text-rose-300">{err}</div>}
      {ok && <div className="text-xs text-lifted-green">Saved.</div>}

      <button
        onClick={save}
        disabled={busy}
        className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="admin-products-save"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        Save changes
      </button>
    </div>
  );
}

function Label({ children }) {
  return <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim mb-1.5">{children}</div>;
}

function FieldText({ label, value, onChange, multiline, icon: Icon }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-lifted-dim" />}
        {multiline ? (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
          />
        ) : (
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
          />
        )}
      </div>
    </div>
  );
}

function FieldNumber({ label, value, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
      />
    </div>
  );
}

function Toggle({ on, onClick, label, tone }) {
  const cls = on
    ? (tone === "rose" ? "bg-rose-500/15 text-rose-200 border-rose-400/40" : "bg-lifted-green/15 text-lifted-green border-lifted-green/40")
    : "border-white/10 text-lifted-dim hover:text-white";
  return (
    <button type="button" onClick={onClick} className={`border rounded-md px-2 py-1.5 text-left ${cls}`}>
      {on ? "✓ " : ""}{label}
    </button>
  );
}

function normalize(p) {
  const images = (p.images || []).map((i) => (typeof i === "string" ? { src: i } : i));
  const media = images.map((i, idx) => ({
    id: i.upload_id || null,
    url: i.src || i.url,
    content_type: i.content_type || (i.src?.endsWith(".mp4") ? "video/mp4" : "image/jpeg"),
    primary: !!i.primary || idx === 0,
  }));
  return {
    handle: p.handle,
    title: p.title || "",
    description: p.description || "",
    vendor: p.vendor || "",
    product_type: p.product_type || "",
    price_min_cents: p.price_min_cents ?? "",
    compare_at_price_cents: p.compare_at_price_cents ?? "",
    section: p.section || "",
    tags: (p.tags || []).join(", "),
    media,
    image_urls_extra: "",
    featured: !!p.featured,
    new_release: !!p.new_release,
    best_seller: !!p.best_seller,
    same_day_eligible: !!p.same_day_eligible,
    hidden: !!p.hidden,
  };
}

function numOrNull(v) {
  if (v === "" || v == null) return null;
  return Number(v);
}
